import './NativeRedactionsSection.scss';
import React from 'react';
import {
  Box,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';
import {Add as AddIcon} from '@mui/icons-material';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';
import {ReactComponent as RadioActiveIcon} from '../../../../../assets/images/radio-active-icon.svg';
import {ReactComponent as RadioEnableIcon} from '../../../../../assets/images/radio-enable-icon.svg';
import {TagItem} from '../../../../../api/types/production.type';
import {ExtendedTreeItemProps} from '../../../../../shared/interfaces';
import {ISelectItem} from '../../../../../shared/types';
import {getRedactionTypeOptions} from '../../CreateNewProductionUtils';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ArrToggle} from '../../../../common/arr-toggle/ArrToggle';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrCheckbox from '../../../../common/arr-checkbox/ArrCheckbox';
import ArrSingleSelectDropdown from '../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import CustomRedactionTextAccordion from '../../common/custom-redaction-text-accordion/CustomRedactionTextAccordion';
import TagsSection from '../../common/tags-section/TagsSection';
import {
  DEFAULT_NATIVE_REDACTION_STYLE,
  DEFAULT_NATIVE_REDACTION_TEXT,
  NativeRedactionItem,
  NativeRedactionSettingsState,
  isNativeTextRedactionStyle,
  normalizeNativeRedactionStyleValue,
} from './nativeRedactionUtils';

interface NativeRedactionsSectionProps {
  settings: NativeRedactionSettingsState;
  redactionGlobalSelectedTags: TagItem[];
  isRedactionWithoutTags: boolean;
  redactionTagTreeData: TreeViewBaseItem<ExtendedTreeItemProps>[];
  redactionStyleOptions: ISelectItem[];
  insertFieldTypes: ISelectItem[];
  metadataFieldOptions: ISelectItem[];
  productionFieldOptions: ISelectItem[];
  disabled?: boolean;
  onSettingsChange: (settings: NativeRedactionSettingsState) => void;
  onGlobalTagsChange: (tags: TagItem[]) => void;
  onRedactionWithoutTagsChange: (checked: boolean) => void;
  onDirty?: () => void;
  getInfoIconA11yProps: (label: string) => Record<string, unknown>;
  viewModeDisabledA11yProps?: Record<string, unknown>;
}

export const NativeRedactionsSection: React.FC<
  NativeRedactionsSectionProps
> = ({
  settings,
  redactionGlobalSelectedTags,
  isRedactionWithoutTags,
  redactionTagTreeData,
  redactionStyleOptions,
  insertFieldTypes,
  metadataFieldOptions,
  productionFieldOptions,
  disabled = false,
  onSettingsChange,
  onGlobalTagsChange,
  onRedactionWithoutTagsChange,
  onDirty,
  getInfoIconA11yProps,
  viewModeDisabledA11yProps = {},
}) => {
  const {t} = useTranslation();
  const nativeRedactionTagsFlyoutTooltip = {
    title: t(
      'productionsAndExports.createNewProduction.productionComponents.native.tooltips.selectTags.title'
    ),
    content: t(
      'productionsAndExports.createNewProduction.productionComponents.native.tooltips.selectTags.content'
    ),
  };
  const redactionTypeOptions = getRedactionTypeOptions();
  const normalizedRedactionStyle = normalizeNativeRedactionStyleValue(
    settings.redactionStyle
  );
  const showRedactionTextSection = isNativeTextRedactionStyle(
    normalizedRedactionStyle
  );
  const selectedRedactionStyleOption =
    redactionStyleOptions.find(
      (item) => String(item.value) === normalizedRedactionStyle
    ) ??
    redactionStyleOptions.find(
      (item) => String(item.value) === DEFAULT_NATIVE_REDACTION_STYLE
    ) ??
    redactionStyleOptions[0];

  const markDirty = () => {
    onDirty?.();
  };

  const updateSettings = (updates: Partial<NativeRedactionSettingsState>) => {
    onSettingsChange({...settings, ...updates});
    markDirty();
  };

  const handleBurnRedactionToggle = (checked: boolean) => {
    if (disabled) return;
    updateSettings({
      isBurnRedactionSelected: checked,
      ...(checked
        ? {
            redactionType: 1,
            isRedactionByAnnotation: true,
            redactionStyle: DEFAULT_NATIVE_REDACTION_STYLE,
          }
        : {}),
    });
  };

  const handleRedactionTypeChange = (value: number) => {
    if (disabled) return;
    updateSettings({
      redactionType: value,
      isRedactionByAnnotation: value === 1,
    });
  };

  const handleRedactionStyleChange = (value: string) => {
    if (disabled) return;
    updateSettings({redactionStyle: value});
  };

  const handleAddRedactionData = () => {
    if (disabled) return;
    const newRedactionData: NativeRedactionItem = {
      id: `redaction-${Date.now()}`,
      redactionText: DEFAULT_NATIVE_REDACTION_TEXT,
      redactionLogicId: 0,
      selectedRedactionList: '',
      selectedTags: [],
    };

    updateSettings({
      redactionDataList: [...settings.redactionDataList, newRedactionData],
    });
  };

  const handleUpdateRedactionData = (
    id: string,
    updates: Partial<NativeRedactionItem>
  ) => {
    if (disabled) return;
    updateSettings({
      redactionDataList: settings.redactionDataList.map((item) =>
        item.id === id ? {...item, ...updates} : item
      ),
    });
  };

  const handleRemoveRedactionData = (id: string) => {
    if (disabled) return;
    updateSettings({
      redactionDataList: settings.redactionDataList.filter(
        (item) => item.id !== id
      ),
    });
  };

  return (
    <Box
      className="redactions-section"
      role="region"
      aria-labelledby="native-redactions-section-title"
      data-testid="native-redactions-section"
    >
      <Box className="main-section-header">
        <Typography
          id="native-redactions-section-title"
          className="typo-text subhead3_size section-title"
          data-testid="native-component-redactions-title"
        >
          {t(
            'productionsAndExports.createNewProduction.productionComponents.native.redactions.title'
          )}
        </Typography>
        <ArrTooltipWrapper
          title={t(
            'productionsAndExports.createNewProduction.productionComponents.native.redactions.tooltip.title'
          )}
          closeTooltipOnEscPress
          trigger={
            <InfoIcon
              fontSize="medium"
              className="info-icon"
              {...getInfoIconA11yProps('Redactions info')}
            />
          }
        >
          <Trans
            i18nKey="productionsAndExports.createNewProduction.productionComponents.native.redactions.tooltip.content"
            components={{
              p: <p data-testid="native-component-redactions-tooltip-p" />,
              strong: (
                <strong data-testid="native-component-redactions-tooltip-strong" />
              ),
              br: <br data-testid="native-component-redactions-tooltip-br" />,
            }}
          />
        </ArrTooltipWrapper>
      </Box>

      <Box className="redactions-content">
        <Box className="redaction-option">
          <Box className="redactions-description">
            <Typography
              className="typo-text body3_size"
              data-testid="native-component-redactions-description"
            >
              <Trans
                i18nKey="productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.description"
                components={{
                  br: (
                    <br data-testid="native-component-redactions-description-br" />
                  ),
                }}
              />
            </Typography>
          </Box>
          <Box className="native-toggle">
            <ArrToggle
              role="switch"
              aria-checked={settings.isBurnRedactionSelected}
              {...viewModeDisabledA11yProps}
              checked={settings.isBurnRedactionSelected}
              onChange={(e) => handleBurnRedactionToggle(e.target.checked)}
              toggleSize="medium"
              disabled={disabled}
              dataTestId="native-burn-redaction-toggle"
            />
            <Typography
              className="toggle-text typo-text subhead4_size"
              data-testid="native-component-burn-redactions-label"
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.burnRedactions.label'
              )}
            </Typography>
          </Box>
        </Box>

        {settings.isBurnRedactionSelected && (
          <>
            <Divider className="section-divider" />

            <Box className="redaction-option specify-redactions-section">
              <Typography
                className="typo-text subhead4_size section-subtitle"
                data-testid="native-component-specify-redactions-title"
              >
                {t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.specifyRedactions.title'
                )}
              </Typography>

              <Box className="redaction-type-selection">
                <Box className="redaction-type-radio-group">
                  <RadioGroup
                    row
                    value={settings.redactionType}
                    onChange={(e) => {
                      const selectedObj = redactionTypeOptions.find(
                        (opt) => opt.value?.toString() === e.target.value
                      );
                      if (selectedObj) {
                        handleRedactionTypeChange(
                          Number(selectedObj.value) || 0
                        );
                      }
                    }}
                    className="custom-radio-group"
                    aria-label={t(
                      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.specifyRedactions.title'
                    )}
                  >
                    {redactionTypeOptions.map(({value, label}, index) => (
                      <FormControlLabel
                        key={value}
                        value={value}
                        control={
                          <Radio
                            icon={<RadioEnableIcon />}
                            checkedIcon={<RadioActiveIcon />}
                            className="typo-text body3_size"
                            inputProps={{
                              'aria-label': String(label),
                            }}
                            disabled={disabled}
                          />
                        }
                        className="typo-text body3_size"
                        label={
                          <span
                            className="typo-text body3_size radio-text"
                            data-testid={`native-component-redaction-type-${index}`}
                          >
                            {label}
                          </span>
                        }
                        disabled={disabled}
                      />
                    ))}
                  </RadioGroup>
                </Box>

                <Box className="redaction-style-dropdown">
                  <Typography
                    className="typo-text body3_size dropdown-title"
                    data-testid="native-component-redaction-style-label"
                  >
                    {t(
                      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionStyle.label'
                    )}
                  </Typography>
                  <ArrSingleSelectDropdown
                    dropdownLabel={t(
                      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionStyle.label'
                    )}
                    dropdownItems={redactionStyleOptions}
                    dropdownItemKey="value"
                    dropdownItemDisplay="label"
                    selectedValues={selectedRedactionStyleOption}
                    onSelectionChange={(item) => {
                      handleRedactionStyleChange(
                        String(item?.value) || DEFAULT_NATIVE_REDACTION_STYLE
                      );
                    }}
                    sizeVariant="medium"
                    dataTestId="native-redaction-style-dropdown"
                    tabIndex={disabled ? -1 : undefined}
                    disabled={disabled}
                  />
                </Box>
              </Box>

              {settings.redactionType === 0 && (
                <Box className="redaction-tags-section">
                  <Box className="tags-config">
                    <TagsSection
                      headerTitle={t(
                        'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionTags.label'
                      )}
                      buttonTitle={t(
                        'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionTags.buttonText'
                      )}
                      flyoutTooltip={nativeRedactionTagsFlyoutTooltip}
                      selectedTags={redactionGlobalSelectedTags}
                      onTagsChange={(tags) => {
                        if (disabled) return;
                        onGlobalTagsChange(tags);
                        markDirty();
                      }}
                      tagsTreeData={redactionTagTreeData}
                      disabled={disabled}
                      moreItemSection={
                        <Box className="redaction-flyout-more-item">
                          <ArrCheckbox
                            data-testid="native-redaction-without-tags"
                            checked={isRedactionWithoutTags}
                            onChange={(checked) => {
                              if (disabled) return;
                              onRedactionWithoutTagsChange(checked);
                              markDirty();
                            }}
                            labelText={t(
                              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionWithoutTags.label'
                            )}
                            className="more-item-checkbox"
                            disabled={disabled}
                            aria-label="native-redaction-without-tags-checkbox"
                          />
                          <ArrTooltipWrapper
                            title={t(
                              'productionsAndExports.createNewProduction.productionComponents.native.redactions.tooltips.redactionWithoutTags.title'
                            )}
                            closeTooltipOnEscPress
                            trigger={
                              <InfoIcon
                                fontSize="medium"
                                className="info-icon"
                                {...getInfoIconA11yProps('Redaction Text info')}
                              />
                            }
                          >
                            <Trans
                              i18nKey="productionsAndExports.createNewProduction.productionComponents.native.redactions.tooltips.redactionWithoutTags.content"
                              components={{
                                p: (
                                  <p data-testid="native-component-redaction-without-tags-p" />
                                ),
                                br: (
                                  <br data-testid="native-component-redaction-without-tags-br" />
                                ),
                              }}
                            />
                          </ArrTooltipWrapper>
                        </Box>
                      }
                    />
                  </Box>
                </Box>
              )}
            </Box>

            {showRedactionTextSection && (
              <>
                <Divider className="section-divider" />

                <Box className="redaction-option redaction-text-section">
                  <Typography
                    className="typo-text subhead4_size section-subtitle"
                    data-testid="native-component-redaction-text-title"
                  >
                    {t(
                      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionText.label'
                    )}
                  </Typography>

                  <Box className="default-redaction-text">
                    <Typography
                      className="typo-text body3_size"
                      data-testid="native-component-default-redaction-text"
                    >
                      {t(
                        'productionsAndExports.createNewProduction.productionComponents.native.redactions.defaultRedactionText'
                      )}
                    </Typography>
                  </Box>

                  {settings.redactionDataList.map((redaction, index) => {
                    const disabledTags = settings.redactionDataList
                      .filter((item) => item.id !== redaction.id)
                      .flatMap((item) => item.selectedTags || []);

                    return (
                      <CustomRedactionTextAccordion
                        key={redaction.id}
                        id={redaction.id}
                        index={index}
                        redactionTextData={{
                          selectedTags: redaction.selectedTags,
                          disabledTags,
                          placeholderText: redaction.redactionText,
                        }}
                        onDelete={handleRemoveRedactionData}
                        onRedactionTextDataChange={(id, data) =>
                          handleUpdateRedactionData(id, {
                            selectedTags: data.selectedTags,
                            redactionText: data.placeholderText,
                          })
                        }
                        tagsTreeData={redactionTagTreeData}
                        insertFieldTypes={insertFieldTypes}
                        metadataFieldOptions={metadataFieldOptions}
                        productionFieldOptions={productionFieldOptions}
                        disabled={disabled}
                        showAbbreviatedText={false}
                        contentColumnCount={2}
                        tagsFlyoutTooltip={nativeRedactionTagsFlyoutTooltip}
                      />
                    );
                  })}

                  {!disabled && (
                    <Box className="add-redaction-data">
                      <ArrButton
                        variant="secondary"
                        size="medium"
                        onClick={handleAddRedactionData}
                        className="typo-text buttonMedium_size"
                        startIcon={<AddIcon />}
                        dataTestId="native-add-redaction-data-button"
                      >
                        {t(
                          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionText.redactionItem.addButton'
                        )}
                        <ArrTooltipWrapper
                          title={t(
                            'productionsAndExports.createNewProduction.productionComponents.native.redactions.tooltips.redactionItemButton.title'
                          )}
                          threshold={-1}
                          closeTooltipOnEscPress
                          trigger={
                            <InfoIcon
                              fontSize="medium"
                              className="info-icon-sm"
                              {...getInfoIconA11yProps('Redaction item info')}
                            />
                          }
                        >
                          <Trans
                            i18nKey="productionsAndExports.createNewProduction.productionComponents.native.redactions.tooltips.redactionItemButton.content"
                            components={{
                              p: (
                                <p data-testid="native-component-redaction-item-tooltip-p" />
                              ),
                              br: (
                                <br data-testid="native-component-redaction-item-tooltip-br" />
                              ),
                            }}
                          />
                        </ArrTooltipWrapper>
                      </ArrButton>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
